import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const Countdown = () => {
  const weddingDate = new Date('2026-02-05T00:00:00').getTime();
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = weddingDate - now;

      setTimeLeft({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000)
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [weddingDate]);

  const countdownItems = [
    { label: 'Days', value: timeLeft.days },
    { label: 'Hours', value: timeLeft.hours },
    { label: 'Minutes', value: timeLeft.minutes },
    { label: 'Seconds', value: timeLeft.seconds }
  ];

  return (
    <section className="py-32 bg-gradient-to-b from-white via-wedding-blush to-white relative overflow-hidden">
      {/* Animated Background Grid */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(#C9A581 1px, transparent 1px), linear-gradient(90deg, #C9A581 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      <div className="container mx-auto px-4 max-w-6xl relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <motion.div 
            className="inline-block mb-6"
            animate={{ rotate: [0, 360] }}
            transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
          >
            <svg width="50" height="50" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="30" fill="none" stroke="#C9A581" strokeWidth="0.5"/>
              <circle cx="50" cy="50" r="20" fill="none" stroke="#8B7355" strokeWidth="0.5"/>
              <circle cx="50" cy="50" r="10" fill="none" stroke="#C9A581" strokeWidth="0.5"/>
            </svg>
          </motion.div>

          <h2 className="text-4xl md:text-6xl font-serif text-wedding-accent mb-4">
            The Countdown
          </h2>
          <div className="flex items-center justify-center gap-3">
            <div className="w-16 h-[1px] bg-wedding-primary"></div>
            <div className="w-2 h-2 bg-wedding-primary rounded-full"></div>
            <div className="w-16 h-[1px] bg-wedding-primary"></div>
          </div>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 max-w-5xl mx-auto">
          {countdownItems.map((item, index) => (
            <motion.div 
              key={index} 
              className="relative group"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              whileHover={{ y: -10 }}
            >
              {/* 3D Card Effect */}
              <div className="relative perspective-1000">
                {/* Shadow Layer */}
                <div className="absolute inset-0 bg-wedding-accent/10 transform translate-y-2 translate-x-2 transition-transform group-hover:translate-y-3 group-hover:translate-x-3"></div>
                
                {/* Main Card */}
                <div className="relative bg-white border border-wedding-primary/20 p-8 md:p-10 transform transition-all duration-300 group-hover:border-wedding-primary/40">
                  {/* Corner Decorations */}
                  <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-wedding-primary"></div>
                  <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-wedding-primary"></div>
                  <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-wedding-primary"></div>
                  <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-wedding-primary"></div>

                  {/* Number */}
                  <motion.div 
                    className="text-5xl md:text-6xl font-serif text-wedding-accent mb-3 relative"
                    animate={{ 
                      scale: item.label === 'Seconds' ? [1, 1.05, 1] : 1 
                    }}
                    transition={{ duration: 1, repeat: item.label === 'Seconds' ? Infinity : 0 }}
                  >
                    {String(item.value).padStart(2, '0')}
                    {/* Subtle glow effect */}
                    <div className="absolute inset-0 text-wedding-primary/20 blur-sm">
                      {String(item.value).padStart(2, '0')}
                    </div>
                  </motion.div>

                  {/* Label */}
                  <div className="relative">
                    <p className="text-xs uppercase tracking-[0.3em] text-wedding-charcoal font-medium">
                      {item.label}
                    </p>
                    <motion.div 
                      className="h-[1px] bg-wedding-primary mt-2"
                      initial={{ scaleX: 0 }}
                      whileInView={{ scaleX: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.5 + index * 0.1 }}
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom Message */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 1 }}
        >
          <p className="text-sm uppercase tracking-[0.3em] text-wedding-accent">
            Until We Say "I Do"
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Countdown;
